import { lazy, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

// project imports
import AdminRouteGuard from '@/utils/route-guard/AdminRouteGuard';
import AuthGuard from '@/utils/route-guard/AuthGuard';
import PermissionGuard, { NoAccess } from '@/utils/route-guard/PermissionGuard';
import MainLayout from '@/layout/MainLayout';
import Loadable from '@/ui-component/Loadable';
import { useAppSelector } from '@/hooks';
import { PATIENT_DETAILS_PERMISSIONS, PERMISSIONS, STATIC_SUBSCRIBER_PERMISSIONS } from '@/utils/permissions';
import { NO_ACCESS_PATH, getUserHomePath } from '@/utils/role';

// page routing
const DesignSystemPage = Loadable(lazy(() => import('views/design-system')));
const PatientListingPage = Loadable(lazy(() => import('views/pages/patients/listing')));
const MyPatientsListingPage = Loadable(lazy(() => import('views/pages/my-patients/listing')));
const PatientSelectionPage = Loadable(lazy(() => import('views/pages/my-patients/patient-selection')));
const MeetingViewPage = Loadable(lazy(() => import('views/pages/meeting-view')));
const MeetingTranscriptionPage = Loadable(lazy(() => import('views/pages/meeting-transcription')));
const MeetingTranscriptionUploadPage = Loadable(lazy(() => import('views/pages/meeting-transcription/upload')));
const MatchVerificationPage = Loadable(lazy(() => import('views/pages/meeting-transcription/verify')));
const PatientWeekendViewPage = Loadable(lazy(() => import('views/pages/patients/weekend-view')));
const PatientDetailsPage = Loadable(lazy(() => import('views/pages/patients/details')));
const CensusPage = Loadable(lazy(() => import('views/pages/census')));
const DartReportPage = Loadable(lazy(() => import('views/pages/dart-report')));
const InsightsPage = Loadable(lazy(() => import('@/views/pages/insights')));
const CareNavigatorDashboardPage = Loadable(lazy(() => import('@/views/pages/handoff')));
const SupervisorPerformanceScorecardPage = Loadable(lazy(() => import('views/pages/supervisor/performance-scorecard')));
const SupervisorScorecardDetailPage = Loadable(lazy(() => import('views/pages/supervisor/performance-scorecard/detail')));
const AdminHospitalListingPage = Loadable(lazy(() => import('views/pages/admin/hospitals')));
const AdminHospitalCreatePage = Loadable(lazy(() => import('views/pages/admin/hospitals/create')));
const AdminAllUsersPage = Loadable(lazy(() => import('views/pages/admin/users')));
const AdminUserAssociationsPage = Loadable(lazy(() => import('views/pages/admin/user-associations')));
const AdminUserCreatePage = Loadable(lazy(() => import('views/pages/admin/users/create')));
const AdminUserDetailsPage = Loadable(lazy(() => import('views/pages/admin/users/details')));
// const AdminReportsDashboardsPage = Loadable(lazy(() => import('views/pages/admin/reports-dashboards')));
const AdminKnowledgeBasePage = Loadable(lazy(() => import('views/pages/admin/knowledge-base')));
const AdminKnowledgeEscalationPhenotypesPage = Loadable(lazy(() => import('views/pages/admin/knowledge-base/escalation-phenotypes')));
const AdminActivityLogsPage = Loadable(lazy(() => import('views/pages/admin/activity-logs')));
const AdminAclPage = Loadable(lazy(() => import('views/pages/admin/acl')));
const AdminStaticSubscribersPage = Loadable(lazy(() => import('@/views/pages/admin/hospitals/static-subscribers')));
const AdminHospitalDetailsPage = Loadable(lazy(() => import('views/pages/admin/hospitals/details')));
const AdminHospitalManageMappingPage = Loadable(lazy(() => import('views/pages/admin/hospitals/mapping')));
const AdminHospitalManageStaffPage = Loadable(lazy(() => import('views/pages/admin/hospitals/staff')));
const AdminHospitalManageStaffCreatePage = Loadable(lazy(() => import('views/pages/admin/hospitals/staff/create')));
const AdminHospitalManageStaffDetailsPage = Loadable(lazy(() => import('views/pages/admin/hospitals/staff/details')));
const AdminHospitalPopulationScopePage = Loadable(lazy(() => import('views/pages/admin/hospitals/population-scope')));
const AdminHospitalPatientOverridePage = Loadable(lazy(() => import('views/pages/admin/hospitals/population-scope/patient-override')));
const AdminHospitalDataDictionaryPage = Loadable(lazy(() => import('views/pages/admin/hospitals/data-dictionary')));
const AdminHospitalConfigPage = Loadable(lazy(() => import('views/pages/admin/hospitals/hospital-config')));

const allow = (permission: string, element: ReactNode) => <PermissionGuard permission={permission}>{element}</PermissionGuard>;

const allowAny = (permissions: readonly string[], element: ReactNode) => <PermissionGuard anyOf={permissions}>{element}</PermissionGuard>;

const allowPatientDetails = (element: ReactNode) => <PermissionGuard anyOf={PATIENT_DETAILS_PERMISSIONS}>{element}</PermissionGuard>;

const RoleBasedHomePage = () => {
    const { user, permissions } = useAppSelector(({ auth }) => auth);
    return <Navigate to={getUserHomePath(user, permissions)} replace />;
};

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
    path: '/',
    element: (
        <AuthGuard>
            <AdminRouteGuard>
                <MainLayout />
            </AdminRouteGuard>
        </AuthGuard>
    ),
    children: [
        {
            path: '/',
            element: <RoleBasedHomePage />
        },
        {
            path: NO_ACCESS_PATH,
            element: <NoAccess />
        },
        {
            path: '/dashboard',
            element: allow(PERMISSIONS.WEEKEND_DISCHARGE_VIEW, <CareNavigatorDashboardPage />)
        },
        {
            path: '/patients',
            element: allow(PERMISSIONS.CENSUS_VIEW, <PatientListingPage />)
        },
        {
            path: '/my-patients',
            element: allow(PERMISSIONS.CCM_VIEW, <MyPatientsListingPage />)
        },
        {
            path: '/my-patients/patient-selection',
            element: allow(PERMISSIONS.PATIENT_ASSIGNMENT_MANAGE, <PatientSelectionPage />)
        },
        {
            path: '/meeting-view',
            element: allow(PERMISSIONS.MEETING_VIEW_VIEW, <MeetingViewPage />)
        },
        {
            path: '/meeting-transcription',
            element: allow(PERMISSIONS.MEETING_TRANSCRIPTION_MANAGE, <MeetingTranscriptionPage />)
        },
        {
            path: '/meeting-transcription/upload',
            element: allow(PERMISSIONS.MEETING_TRANSCRIPTION_MANAGE, <MeetingTranscriptionUploadPage />)
        },
        {
            path: '/meeting-transcription/:hospitalId/:sessionId/verify',
            element: allow(PERMISSIONS.MEETING_TRANSCRIPTION_MANAGE, <MatchVerificationPage />)
        },
        {
            path: '/supervisor/scorecard',
            element: allow(PERMISSIONS.SCORECARD_VIEW, <SupervisorPerformanceScorecardPage />)
        },
        {
            path: '/supervisor/scorecard/:userId/:unitId/:date',
            element: allow(PERMISSIONS.SCORECARD_VIEW, <SupervisorScorecardDetailPage />)
        },
        {
            path: '/patients/weekend-view/:patientId/:fin/:roomBed',
            element: allow(PERMISSIONS.WEEKEND_DISCHARGE_VIEW, <PatientWeekendViewPage />)
        },
        {
            path: '/patients/:id',
            element: allowPatientDetails(<PatientDetailsPage />)
        },
        {
            path: '/census',
            element: allow(PERMISSIONS.CENSUS_VIEW, <CensusPage />)
        },
        {
            path: '/dart-report',
            element: allow(PERMISSIONS.DART_REPORT_VIEW, <DartReportPage />)
        },
        {
            path: '/insights',
            element: allow(PERMISSIONS.INSIGHTS_VIEW, <InsightsPage />)
        },
        {
            path: '/static-subscribers',
            element: allowAny(STATIC_SUBSCRIBER_PERMISSIONS, <AdminStaticSubscribersPage />)
        },
        {
            path: '/acl',
            element: allow(PERMISSIONS.ACL_MANAGE, <AdminAclPage />)
        },
        {
            path: '/manage-staff',
            element: allow(PERMISSIONS.STAFF_MANAGE, <AdminHospitalManageStaffPage />)
        },
        {
            path: '/manage-staff/create',
            element: allow(PERMISSIONS.STAFF_MANAGE, <AdminHospitalManageStaffCreatePage />)
        },
        {
            path: '/manage-staff/:userId/user-detail',
            element: allow(PERMISSIONS.STAFF_MANAGE, <AdminHospitalManageStaffDetailsPage />)
        },
        // Admin routes
        {
            path: '/admin/hospitals',
            element: <AdminHospitalListingPage />
        },
        {
            path: '/admin/hospitals/create',
            element: <AdminHospitalCreatePage />
        },
        {
            path: '/admin/users',
            element: <AdminAllUsersPage />
        },
        {
            path: '/admin/user-associations',
            element: <AdminUserAssociationsPage />
        },
        {
            path: '/admin/users/create',
            element: <AdminUserCreatePage />
        },
        {
            path: '/admin/users/:id/user-detail',
            element: <AdminUserDetailsPage />
        },
        // {
        //     path: '/admin/reports-dashboards',
        //     element: <AdminReportsDashboardsPage />
        // },
        {
            path: '/admin/meeting-transcription',
            element: allow(PERMISSIONS.MEETING_TRANSCRIPTION_MANAGE, <MeetingTranscriptionPage />)
        },
        {
            path: '/admin/meeting-transcription/upload',
            element: allow(PERMISSIONS.MEETING_TRANSCRIPTION_MANAGE, <MeetingTranscriptionUploadPage />)
        },
        {
            path: '/admin/meeting-transcription/:hospitalId/upload',
            element: allow(PERMISSIONS.MEETING_TRANSCRIPTION_MANAGE, <MeetingTranscriptionUploadPage />)
        },
        {
            path: '/admin/meeting-transcription/:hospitalId/:sessionId/verify',
            element: allow(PERMISSIONS.MEETING_TRANSCRIPTION_MANAGE, <MatchVerificationPage />)
        },
        {
            path: '/admin/knowledge-base',
            element: <AdminKnowledgeBasePage />
        },
        {
            path: '/admin/knowledge-base/escalation-phenotypes',
            element: <AdminKnowledgeEscalationPhenotypesPage />
        },
        {
            path: '/activity-logs',
            element: <AdminActivityLogsPage />
        },
        {
            path: '/admin/acl',
            element: allow(PERMISSIONS.ACL_MANAGE, <AdminAclPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/hospital-details',
            element: <AdminHospitalDetailsPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/manage-mapping',
            element: <AdminHospitalManageMappingPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/dashboard',
            element: allow(PERMISSIONS.WEEKEND_DISCHARGE_VIEW, <CareNavigatorDashboardPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/manage-patients',
            element: allow(PERMISSIONS.CENSUS_VIEW, <PatientListingPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/my-patients',
            element: allow(PERMISSIONS.CCM_VIEW, <MyPatientsListingPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/my-patients/patient-selection',
            element: allow(PERMISSIONS.PATIENT_ASSIGNMENT_MANAGE, <PatientSelectionPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/manage-patients/weekend-view/:patientId/:fin/:roomBed',
            element: allow(PERMISSIONS.WEEKEND_DISCHARGE_VIEW, <PatientWeekendViewPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/manage-patients/:id',
            element: allowPatientDetails(<PatientDetailsPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/census',
            element: allow(PERMISSIONS.CENSUS_VIEW, <CensusPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/dart-report',
            element: allow(PERMISSIONS.DART_REPORT_VIEW, <DartReportPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/insights',
            element: allow(PERMISSIONS.INSIGHTS_VIEW, <InsightsPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/meeting-view',
            element: allow(PERMISSIONS.MEETING_VIEW_VIEW, <MeetingViewPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/meeting-transcription',
            element: allow(PERMISSIONS.MEETING_TRANSCRIPTION_MANAGE, <MeetingTranscriptionPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/meeting-transcription/upload',
            element: allow(PERMISSIONS.MEETING_TRANSCRIPTION_MANAGE, <MeetingTranscriptionUploadPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/meeting-transcription/:sessionId/verify',
            element: allow(PERMISSIONS.MEETING_TRANSCRIPTION_MANAGE, <MatchVerificationPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/meeting-view/patient-details/:id',
            element: allowPatientDetails(<PatientDetailsPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/scorecard',
            element: allow(PERMISSIONS.SCORECARD_VIEW, <SupervisorPerformanceScorecardPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/scorecard/:userId/:unitId/:date',
            element: allow(PERMISSIONS.SCORECARD_VIEW, <SupervisorScorecardDetailPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/manage-staff',
            element: allow(PERMISSIONS.STAFF_MANAGE, <AdminHospitalManageStaffPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/manage-staff/create',
            element: allow(PERMISSIONS.STAFF_MANAGE, <AdminHospitalManageStaffCreatePage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/manage-staff/:userId/user-detail',
            element: allow(PERMISSIONS.STAFF_MANAGE, <AdminHospitalManageStaffDetailsPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/static-subscribers',
            element: allowAny(STATIC_SUBSCRIBER_PERMISSIONS, <AdminStaticSubscribersPage />)
        },
        {
            path: '/admin/hospitals/:hospitalId/population-scope',
            element: <AdminHospitalPopulationScopePage />
        },
        {
            path: '/admin/hospitals/:hospitalId/population-scope/patient-override/:overrideId',
            element: <AdminHospitalPatientOverridePage />
        },
        {
            path: '/admin/hospitals/:hospitalId/population-scope/patient-override',
            element: <AdminHospitalPatientOverridePage />
        },
        {
            path: '/admin/hospitals/:hospitalId/data-dictionary',
            element: <AdminHospitalDataDictionaryPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/hospital-config',
            element: <AdminHospitalConfigPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/playbooks',
            element: <AdminKnowledgeBasePage />
        },
        {
            path: '/admin/hospitals/:hospitalId/playbooks/escalation-phenotypes',
            element: <AdminKnowledgeEscalationPhenotypesPage />
        },

        ...(import.meta.env.DEV
            ? [
                  {
                      path: '/design-system',
                      element: <DesignSystemPage />
                  },
                  {
                      path: '/design-system/:componentId',
                      element: <DesignSystemPage />
                  }
              ]
            : [])
    ]
};

export default MainRoutes;
